import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  Users, 
  Calendar, 
  Clock, 
  FileText, 
  Banknote, 
  TrendingUp, 
  BarChart3, 
  Settings, 
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Shield,
  Languages,
  Upload,
  Globe,
  FileSignature,
  MessageCircle,
  Cpu,
  Database,
  MessageSquare,
  Inbox,
  Volume2,
  VolumeX,
  Timer,
  UserCheck,
  GraduationCap,
  Network
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LogoFullHorizontal } from './Logo';
import AIChatbot from './AIChatbot';
import NotificationBell from './NotificationBell';
import SupportRenewalBanner from './SupportRenewalBanner';
import SupportStatus from './SupportStatus';

const API = process.env.REACT_APP_BACKEND_URL;

// Notification sound (short pleasant chime)
const NOTIFICATION_SOUND_URL = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYNBrzxAAAAAAD/+9DEAAAGAAGn9AAAIi4eL/UMAAEAAA0gwAABM4vjk2YN7Db/z++r1f/U91f/p8nzfPy/MqyufN/z83/hM//5OT5Pj3kxOc5OgABKQYjCxkXKOUYJEGZ4cqVKKEYI8YBAIQZgeVBQFCgsMaDADAkCwsOHAxAaGQsAYBIGBgaCAwJhQJBIaBQOBwKCwSFgoEgsIhIHEQeFAsEg8IAkKBQJCQSDwSBwaDQeFAkEhAEgoNA4GCQOCgSCwiDQiCQkFh0EhMKhIKBQOBISCASDwaFQkIgkJA4IAgHA4KCILCQYEAkKhYPCINCoRCYbB4VCAOCAaFQgEg0Hg8KhENhwKhMIBwQBQSFQkFg4IgsJhwNBgQCgSDgeDAqEg0IBQOCQQDgYFQkGhMLCQPCAYEAsEA8HhMJBQMCYaDQeEQiGxIJBYMCAYEwmFgoHA0JhILBQOB4TCIQDAoGhIIBQOCIUCwYFAuGg8IAoKhYMBoRCAYEgoEg0JBYOCIRCQaDQiFgoHhIKBYNCYSCwcEwqEgsIBQOCYRCYYDQiEgkGhAJhoTCgSDAkGhIJBIOCIRCIZDYeFgsFg4JhYJBYOCYRCYbDYaDQmEgsGBMKhIMCQaDQiFg0JBQKBoPCYTDAeEwkFA0HhMLhkNB4RCYaDweEwuGg8IhMNB4RCITCwYEwoFg0JhYLBoRCQYDggFAoGhMJBYNCISCwcEQoFg4JhQKBgSCgWDQoEg0HhIJBYNCITDYcDQiEgsGA0IhQJBYOCAUCgaEwmFg0IhILBwSCwaDQmFQsHBEJBYMCYUCwYEgwJBQOCITDQgEg0IhILBgTCoWDgiEgsGBIMCQWDQiEgkGg8JhYMCISCwYEgoGhEJBYMCIUCwYEwoFAoGhIKBYOCYTCgYEgoFg4IhMNhoQCQUDQmFAoGBIKBQMCQYEgoGhMIhILBwRCgWDgmFQsGhMJBYOCAUCgaEQkFg4JhUKBoRCgSDAgFAoGhMJBYNCYTCgaDwmFgsGhIKBYNCYUDAeEgoGhIJBgRCgaDgmFg0IhILBwRCQWDgmFAoGA8JBYOCITDQeEQkFg4JhULBoTCgWDgmFAsGBEKBYNCISCwYEgoFAoHBEJBYOCYUCwaEQoEgwIhQLBgRCQWDAiFAoGhIKBgQCQUCwcEQoFg0JBINCIUCQYEQkFg0JhMKBYNCYTCgUDAkFAsGhEJBYOCITCwaEQkFg4JhQLBgRCQSDgiFAsGBEKBQKBgSCgWDQiEgoGhMJhYNCYUCQaEQkFgwIhQJBYNCISCwYEQkFA0IBIJBoTCgWDAmFAoGhEJBYMCISCwcEQkFgwJhQLBgRCQSDgiEgsGBEJBINB4SCwaEQkFgwIhILBgRCQWDAiFAoGhEJBYMCISCwYEQkFgwIhILBgQCQUCwYEQkEg0HhIJBYMCISCwYEQkFgwIhILBgRCQWDAAD/+9DEAAAL3AGf9AgAI0odt/NPAAH//of//p//////+n//9D/U';

export default function DashboardLayout({ children, user }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hrSupportUnread, setHrSupportUnread] = useState(0);
  const [notificationsMuted, setNotificationsMuted] = useState(() => {
    return localStorage.getItem('hrms_notifications_muted') === 'true';
  });
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);
  const isFirstLoad = useRef(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!notificationsMuted && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  }, [notificationsMuted]);

  // Toggle mute state
  const toggleMute = () => {
    const newMuted = !notificationsMuted;
    setNotificationsMuted(newMuted);
    localStorage.setItem('hrms_notifications_muted', String(newMuted));
    toast.success(newMuted ? 'Notifications muted' : 'Notifications unmuted');
  };

  // Fetch HR Support unread count with notifications
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/api/hr-support/unread-count`);
        const newCount = response.data.unread_count || 0;
        
        // Check if we have new messages (only after first load)
        if (!isFirstLoad.current && newCount > prevUnreadRef.current) {
          const newMessages = newCount - prevUnreadRef.current;
          playNotificationSound();
          toast.info(
            `${newMessages} new HR message${newMessages > 1 ? 's' : ''}`,
            {
              description: 'Click to view your messages',
              action: {
                label: 'View',
                onClick: () => navigate(user?.role === 'employee' ? '/dashboard/hr-support' : '/dashboard/hr-inbox')
              }
            }
          );
        }
        
        prevUnreadRef.current = newCount;
        setHrSupportUnread(newCount);
        isFirstLoad.current = false;
      } catch (error) {
        // Silently fail - user might not have access
        console.debug('Could not fetch HR support unread count');
      }
    };

    // Initial fetch
    fetchUnreadCount();

    // Poll every 30 seconds for new messages
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [playNotificationSound, navigate, user?.role]);

  // Iter58 — License-feature gating for sidebar visibility.
  // Tester (Ankur, 28-Jun) reported all modules remained visible even when
  // the installed license only included a feature subset. We now fetch
  // `/api/license/status`, build a Set of licensed feature names, and the
  // navigation builder below filters items that declare a `feature` they
  // require. Items without a `feature` (Dashboard, Settings, License
  // Management) are always visible.
  const [licensedFeatures, setLicensedFeatures] = useState(null); // null = not yet loaded
  useEffect(() => {
    let cancelled = false;
    const fetchLicense = async () => {
      try {
        const res = await axios.get(`${API}/api/license/status`);
        if (cancelled) return;
        if (res.data?.installed && Array.isArray(res.data?.features)) {
          setLicensedFeatures(new Set(res.data.features));
        } else {
          // No license installed → fall back to "everything visible". This
          // matches the soft-mode behaviour the validator already uses for
          // un-licensed deployments so devs aren't locked out on day 1.
          setLicensedFeatures(null);
        }
      } catch {
        setLicensedFeatures(null);
      }
    };
    fetchLicense();
    // Refresh every 5 min so a freshly installed/replaced license is
    // reflected without a full page reload.
    const interval = setInterval(fetchLicense, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const handleLogout = () => {
    // TC-AUTH-033: Clear auth from both storages on logout
    localStorage.removeItem('hrms_token');
    localStorage.removeItem('hrms_user');
    sessionStorage.removeItem('hrms_token');
    sessionStorage.removeItem('hrms_user');
    navigate('/login');
  };

  // Iter58 — items missing a `feature` key are ALWAYS visible. Items with
  // a `feature` key are only shown when that feature is in the active
  // license's feature list (or when no license is installed at all).
  const isFeatureLicensed = (feature) => {
    if (!feature) return true;
    if (licensedFeatures === null) return true; // un-licensed soft mode
    return licensedFeatures.has(feature);
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const userRole = user?.role;
    const userEmail = user?.email;
    const isEmployee = userRole === 'employee';
    const isManagerType = ['manager', 'department_head'].includes(userRole);
    
    // Platform/Super Admin - only these specific emails get limited platform-focused menu
    const superAdminEmails = ['admin@myhrorbit.com', 'superadmin@myhrorbit.com'];
    const isSuperAdmin = userRole === 'platform_admin' || userRole === 'super_user' || superAdminEmails.includes(userEmail);
    
    // HR Admin - full HR access (includes hr@myhrorbit.com)
    const isHRAdmin = ['admin', 'hr_manager', 'hr_user'].includes(userRole) && !isSuperAdmin;
    
    if (isEmployee) {
      return [
        { name: t('nav.dashboard'), href: '/dashboard/', icon: LayoutDashboard },
        { name: t('nav.myProfile'), href: '/dashboard/my-profile', icon: Users, feature: 'employees' },
        { name: t('nav.leave'), href: '/dashboard/leave', icon: Calendar, feature: 'leave' },
        { name: t('nav.attendance'), href: '/dashboard/attendance', icon: Clock, feature: 'attendance' },
        { name: t('nav.claims'), href: '/dashboard/claims', icon: FileText, feature: 'claims' },
        { name: t('nav.payslips'), href: '/dashboard/payslips', icon: Banknote, feature: 'payroll' },
        { name: t('nav.documents'), href: '/dashboard/my-documents', icon: FileSignature, feature: 'documents' },
        { name: t('nav.performance'), href: '/dashboard/performance', icon: TrendingUp, feature: 'performance' },
        { name: t('nav.hrInbox'), href: '/dashboard/hr-support', icon: MessageSquare },
        { name: t('nav.settings'), href: '/dashboard/employee-settings', icon: Settings },
      ].filter(i => isFeatureLicensed(i.feature));
    }
    
    if (isManagerType) {
      // Managers are employees with one extra module: My Team
      return [
        { name: t('nav.dashboard'), href: '/dashboard/', icon: LayoutDashboard },
        { name: t('nav.myProfile'), href: '/dashboard/my-profile', icon: Users, feature: 'employees' },
        { name: t('nav.myTeam'), href: '/dashboard/my-team', icon: Users, feature: 'employees' },
        { name: t('nav.leave'), href: '/dashboard/leave', icon: Calendar, feature: 'leave' },
        { name: t('nav.attendance'), href: '/dashboard/attendance', icon: Clock, feature: 'attendance' },
        { name: t('nav.claims'), href: '/dashboard/claims', icon: FileText, feature: 'claims' },
        { name: t('nav.payslips'), href: '/dashboard/payslips', icon: Banknote, feature: 'payroll' },
        { name: t('nav.documents'), href: '/dashboard/my-documents', icon: FileSignature, feature: 'documents' },
        { name: t('nav.performance'), href: '/dashboard/performance', icon: TrendingUp, feature: 'performance' },
        { name: t('nav.hrInbox'), href: '/dashboard/hr-support', icon: MessageSquare },
        { name: t('nav.settings'), href: '/dashboard/employee-settings', icon: Settings },
      ].filter(i => isFeatureLicensed(i.feature));
    }
    
    // Super Admin / Platform Admin - focused on platform management (limited menu)
    if (isSuperAdmin) {
      return [
        { name: t('nav.dashboard'), href: '/dashboard/', icon: LayoutDashboard },
        { name: t('nav.employees'), href: '/dashboard/employees', icon: Users },
        { name: t('nav.reports'), href: '/dashboard/reports', icon: BarChart3 },
        { name: t('nav.settings'), href: '/dashboard/settings', icon: Settings },
      ];
    }
    
    // HR Admin navigation (full HR access) - this is for hr@myhrorbit.com
    if (isHRAdmin) {
      return [
        { name: t('nav.dashboard'), href: '/dashboard/', icon: LayoutDashboard },
        { name: t('nav.employees'), href: '/dashboard/employees', icon: Users, feature: 'employees' },
        { name: t('nav.leave'), href: '/dashboard/leave', icon: Calendar, feature: 'leave' },
        { name: t('nav.attendance'), href: '/dashboard/attendance', icon: Clock, feature: 'attendance' },
        { name: t('nav.claims'), href: '/dashboard/claims', icon: FileText, feature: 'claims' },
        { name: t('nav.payroll'), href: '/dashboard/payroll', icon: Banknote, feature: 'payroll' },
        { name: t('nav.payslips'), href: '/dashboard/payslips', icon: Banknote, feature: 'payroll' },
        // Iter60 (Ankur 30-Jun-2026) — overtime / probation / forms were
        // previously gated against features included in Trial (attendance,
        // employees, reports), so they stayed visible on a Trial install.
        // Re-pointing them to features only available in higher tiers so
        // Trial sidebar matches the advertised "7 core" feature set.
        { name: t('nav.overtime'), href: '/dashboard/overtime', icon: Timer, feature: 'migration' },
        { name: t('nav.probation'), href: '/dashboard/probation', icon: UserCheck, feature: 'migration' },
        { name: t('nav.performance'), href: '/dashboard/performance', icon: TrendingUp, feature: 'performance' },
        { name: t('nav.documents'), href: '/dashboard/documents', icon: FileSignature, feature: 'documents' },
        { name: t('nav.reports'), href: '/dashboard/reports', icon: BarChart3, feature: 'reports' },
        { name: t('nav.forms'), href: '/dashboard/forms', icon: FileText, feature: 'documents' },
        { name: t('nav.hrInbox'), href: '/dashboard/hr-inbox', icon: Inbox },
        { name: t('nav.import'), href: '/dashboard/import', icon: Upload, feature: 'migration' },
        { name: t('nav.migration'), href: '/dashboard/migration', icon: Database, feature: 'migration' },
        { name: t('nav.settings'), href: '/dashboard/settings', icon: Settings },
        { name: t('nav.aiConfig'), href: '/dashboard/ai-config', icon: Cpu, feature: 'ai_features' },
        { name: t('nav.licenseSupport'), href: '/dashboard/license-support', icon: Shield },
        { name: t('nav.psclAdmin'), href: '/dashboard/pscl-admin', icon: Shield },
      ].filter(i => isFeatureLicensed(i.feature));
    }
    
    // Default admin navigation (fallback - full access)
    return [
      { name: t('nav.dashboard'), href: '/dashboard/', icon: LayoutDashboard },
      { name: t('nav.employees'), href: '/dashboard/employees', icon: Users, feature: 'employees' },
      { name: t('nav.leave'), href: '/dashboard/leave', icon: Calendar, feature: 'leave' },
      { name: t('nav.attendance'), href: '/dashboard/attendance', icon: Clock, feature: 'attendance' },
      { name: t('nav.claims'), href: '/dashboard/claims', icon: FileText, feature: 'claims' },
      { name: t('nav.payroll'), href: '/dashboard/payroll', icon: Banknote, feature: 'payroll' },
      { name: t('nav.payslips'), href: '/dashboard/payslips', icon: Banknote, feature: 'payroll' },
      { name: t('nav.overtime'), href: '/dashboard/overtime', icon: Timer, feature: 'migration' },
      { name: t('nav.probation'), href: '/dashboard/probation', icon: UserCheck, feature: 'migration' },
      { name: t('nav.performance'), href: '/dashboard/performance', icon: TrendingUp, feature: 'performance' },
      { name: t('nav.documents'), href: '/dashboard/documents', icon: FileSignature, feature: 'documents' },
      { name: t('nav.reports'), href: '/dashboard/reports', icon: BarChart3, feature: 'reports' },
      { name: t('nav.forms'), href: '/dashboard/forms', icon: FileText, feature: 'documents' },
      { name: t('nav.import'), href: '/dashboard/import', icon: Upload, feature: 'migration' },
      { name: t('nav.migration'), href: '/dashboard/migration', icon: Database, feature: 'migration' },
      { name: t('nav.settings'), href: '/dashboard/settings', icon: Settings },
      { name: t('nav.aiConfig'), href: '/dashboard/ai-config', icon: Cpu, feature: 'ai_features' },
      { name: t('nav.licenseSupport'), href: '/dashboard/license-support', icon: Shield },
      { name: t('nav.psclAdmin'), href: '/dashboard/pscl-admin', icon: Shield },
    ].filter(i => isFeatureLicensed(i.feature));
  };

  const navigation = getNavigationItems();

  // ---- RBAC: filter navigation by user.modules (if persisted) and hide
  // internal/platform-only items from customer roles. ----
  // Map nav href -> the module it gates against. Items not in the map are
  // always shown (Dashboard, profile-style links). Internal items are hidden
  // unless the role is platform_admin.
  // Iter58 — Settings was being dropped by `user.modules` RBAC when the
  // persisted modules list lacked 'settings'. That broke logging-in users
  // whose admin had granular module assignments. Settings is a baseline
  // page (password / preferences) that every authenticated user needs, so
  // it's now explicitly OFF the module-gate map.
  const NAV_MODULE_MAP = {
    '/dashboard/employees': 'employees',
    '/dashboard/leave': 'leave',
    '/dashboard/attendance': 'attendance',
    '/dashboard/claims': 'claims',
    '/dashboard/payroll': 'payroll',
    '/dashboard/payslips': 'payroll',
    '/dashboard/overtime': 'payroll',
    '/dashboard/performance': 'performance',
    '/dashboard/reports': 'reports',
  };
  const INTERNAL_ONLY_HREFS = new Set([
    '/dashboard/pscl-admin',
    '/dashboard/license-support',
    '/dashboard/ai-config',
    '/dashboard/migration',
  ]);
  const callerRole = user?.role;
  const userModules = Array.isArray(user?.modules) ? user.modules : [];
  const filteredNavigation = navigation.filter((item) => {
    // Hide internal/platform routes from customer roles.
    if (INTERNAL_ONLY_HREFS.has(item.href) && callerRole !== 'platform_admin') {
      return false;
    }
    // If user.modules is set, enforce it for module-gated routes.
    const moduleGate = NAV_MODULE_MAP[item.href];
    if (moduleGate && userModules.length > 0 && !userModules.includes(moduleGate)) {
      return false;
    }
    return true;
  });

  const isActive = (path) => {
    if (path === '/dashboard/') return location.pathname === '/dashboard' || location.pathname === '/dashboard/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 transition-transform duration-300 ease-in-out`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-800">
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <LogoFullHorizontal />
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              // Show unread badge for HR Support (employees) or HR Inbox (HR admins)
              const showBadge = (item.name === 'HR Support' || item.name === 'HR Inbox') && hrSupportUnread > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center justify-between px-3 py-2 rounded-xl transition-all duration-200 text-sm ${
                    active
                      ? 'bg-emerald-500/10 text-emerald-400 shadow-lg shadow-emerald-500/20'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  {showBadge && (
                    <span 
                      className="min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs font-bold bg-emerald-500 text-white rounded-full animate-pulse"
                      data-testid={`${item.name.toLowerCase().replace(/\s+/g, '-')}-unread-badge`}
                    >
                      {hrSupportUnread > 99 ? '99+' : hrSupportUnread}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User profile */}
          <div className="p-3 border-t border-slate-800">
            <div className="flex items-center space-x-2 mb-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-semibold text-xs">
                  {user?.full_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-400 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center space-x-2 w-full px-3 py-1.5 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Support Renewal Banner (Owner only) */}
        <SupportRenewalBanner />
        
        {/* Top bar */}
        <div className="sticky top-0 z-40 min-h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 px-3 py-2 sm:px-6 sm:py-0 transition-colors duration-300" data-testid="dashboard-topbar">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden shrink-0 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 min-w-0 flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap ml-1 sm:ml-4 lg:ml-0">
            <h1 className="text-base sm:text-xl font-bold text-slate-900 dark:text-slate-100 truncate min-w-0" data-testid="topbar-title">
              {navigation.find((item) => isActive(item.href))?.name || 'Dashboard'}
            </h1>
            <div className="flex items-center gap-0.5 sm:gap-3 flex-wrap justify-end min-w-0" data-testid="topbar-controls">
              {/* Language Toggle Button */}
              <button
                onClick={toggleLanguage}
                className="p-2 sm:p-2.5 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all duration-200 group flex items-center space-x-1"
                title={language === 'en' ? 'Switch to Bahasa Malaysia' : 'Switch to English'}
                data-testid="language-toggle-btn"
              >
                <Languages className="h-5 w-5" />
                <span className="text-xs font-medium uppercase">{language}</span>
              </button>
              
              {/* Support Status Badge - Link to AurousOrbit.ai */}
              {(user?.role === 'admin' || user?.role === 'hr_manager') && (
                <div className="hidden md:block"><SupportStatus API={API} /></div>
              )}
              
              {/* Notification Sound Toggle */}
              <button
                onClick={toggleMute}
                className={`p-2 sm:p-2.5 rounded-xl transition-all duration-200 group ${
                  notificationsMuted 
                    ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10' 
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                title={notificationsMuted ? 'Unmute notifications' : 'Mute notifications'}
                data-testid="notification-sound-toggle"
              >
                {notificationsMuted ? (
                  <VolumeX className="h-5 w-5" />
                ) : (
                  <Volume2 className="h-5 w-5" />
                )}
              </button>
              
              {/* Theme Toggle Button */}
              <button
                onClick={toggleTheme}
                className="p-2 sm:p-2.5 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all duration-200 group"
                title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
                data-testid="theme-toggle-btn"
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                ) : (
                  <Sun className="h-5 w-5 group-hover:rotate-90 transition-transform" />
                )}
              </button>
              
              {/* Notification Bell */}
              <NotificationBell />
              
              <span className="hidden md:inline text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap" data-testid="topbar-date">
                {new Date().toLocaleDateString('en-MY', { 
                  weekday: 'short', 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6 md:p-10 bg-slate-50 dark:bg-slate-900 min-h-[calc(100vh-4rem)] transition-colors duration-300">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* AI Chatbot - Available on all dashboard pages */}
      <AIChatbot />
    </div>
  );
}